import { fetchProductCategories, fetchShopCategories, fetchUserCategories, type InsightCategoryOption } from '../api';
import { useAppStore } from './index';
import { isSameShopContext } from '../utils/shopContext';

let productCategoriesRequest: Promise<InsightCategoryOption[]> | null = null;
let productCategoriesRequestVersion = 0;
let productCategoriesRequestKey = '';
let shopCategoriesRequest: Promise<InsightCategoryOption[]> | null = null;
let shopCategoriesRequestVersion = 0;
let shopCategoriesRequestKey = '';

// 退出登录时清除模块级请求缓存，避免切换账号后沿用旧类目数据
export function clearProductCategoriesCaches(): void {
  productCategoriesRequest = null;
  productCategoriesRequestVersion = 0;
  productCategoriesRequestKey = '';
  shopCategoriesRequest = null;
  shopCategoriesRequestVersion = 0;
  shopCategoriesRequestKey = '';
}

function normalizeProductCategoryOptions(options: InsightCategoryOption[]): InsightCategoryOption[] {
  return options.filter((option) => option.label && option.value);
}

function hasMatchedProductCategoryShopContext(shopId: string): boolean {
  const requestedShopId = shopId.trim();
  if (!requestedShopId) {
    return true;
  }

  const store = useAppStore.getState();
  if (store.dataSpaceState.dataSpace === 'DEMO') {
    return isSameShopContext(store.dataSpaceState.demoWorkspace?.shop, requestedShopId);
  }

  return store.shopContexts.some((item) => isSameShopContext(item, requestedShopId));
}

// 页面上的“关注类目”都来自当前系统账号下的货品类目并集，
// 统一放到全局 store 中，避免每个页面各自请求一遍。
export async function ensureProductCategories(options: { force?: boolean; shopId?: string } = {}): Promise<InsightCategoryOption[]> {
  const store = useAppStore.getState();
  const requestedShopId = (options.shopId ?? store.currentShopId ?? '').trim();
  const requestKey = requestedShopId || '__all__';
  const hasMatchedShopContext = hasMatchedProductCategoryShopContext(requestedShopId);

  if (requestedShopId && !hasMatchedShopContext) {
    store.setProductCategoriesOptions(requestedShopId, []);
    return [];
  }

  if (
    !options.force
    && store.productCategories.shopId === requestedShopId
    && store.productCategories.options.length > 0
  ) {
    return store.productCategories.options;
  }

  if (productCategoriesRequest && productCategoriesRequestKey === requestKey) {
    return productCategoriesRequest;
  }

  store.setProductCategoriesLoading(requestedShopId, true);
  const requestVersion = ++productCategoriesRequestVersion;
  productCategoriesRequestKey = requestKey;
  const nextRequest = fetchProductCategories(
    requestedShopId ? { shopIds: [requestedShopId] } : undefined
  )
    .then((response) => {
      const nextOptions = Array.isArray(response.list)
        ? normalizeProductCategoryOptions(
          response.list.map((item) => ({
            value: item.categoryId,
            label: item.label || item.fullLabel || item.categoryId,
            fullLabel: item.fullLabel || item.label || item.categoryId,
          }))
        )
        : [];

      // force 刷新需要覆盖旧请求结果，这里只让最后一次请求写回全局类目。
      if (requestVersion === productCategoriesRequestVersion) {
        useAppStore.getState().setProductCategoriesOptions(requestedShopId, nextOptions);
      }
      return nextOptions;
    })
    .catch((error: unknown) => {
      const nextMessage = error instanceof Error ? error.message : '加载关注类目失败';
      if (requestVersion === productCategoriesRequestVersion) {
        useAppStore.getState().setProductCategoriesError(requestedShopId, nextMessage);
      }
      throw error;
    })
    .finally(() => {
      if (productCategoriesRequest === nextRequest) {
        productCategoriesRequest = null;
        productCategoriesRequestKey = '';
      }
    });

  productCategoriesRequest = nextRequest;
  return nextRequest;
}

export async function ensureShopOperatingCategories(options: {
  force?: boolean;
  shopId?: string;
  scope?: 'current' | 'user';
} = {}): Promise<InsightCategoryOption[]> {
  const store = useAppStore.getState();
  const requestedShopId = options.scope === 'user'
    ? ''
    : (options.shopId ?? store.currentShopId ?? '').trim();
  const requestKey = options.scope === 'user' ? '__user__' : (requestedShopId || '__all__');
  const hasMatchedShopContext = hasMatchedProductCategoryShopContext(requestedShopId);

  if (requestedShopId && !hasMatchedShopContext) {
    store.setShopOperatingCategoriesOptions({
      shopId: requestedShopId,
      options: [],
      status: '',
      confirmed: false,
      recommendedOptions: [],
      maxCount: 5,
    });
    return [];
  }

  if (
    !options.force
    && store.shopOperatingCategories.shopId === requestedShopId
    && store.shopOperatingCategories.options.length > 0
  ) {
    return store.shopOperatingCategories.options;
  }

  if (shopCategoriesRequest && shopCategoriesRequestKey === requestKey) {
    return shopCategoriesRequest;
  }

  store.setShopOperatingCategoriesLoading(requestedShopId, true);
  const requestVersion = ++shopCategoriesRequestVersion;
  shopCategoriesRequestKey = requestKey;
  const nextRequest = (requestedShopId
    ? fetchShopCategories({ shopIds: [requestedShopId] })
    : fetchUserCategories())
    .then((response) => {
      const nextOptions = Array.isArray(response.list)
        ? normalizeProductCategoryOptions(
          response.list.map((item) => ({
            value: item.categoryId,
            label: item.label || item.fullLabel || item.categoryId,
            fullLabel: item.fullLabel || item.label || item.categoryId,
          }))
        )
        : [];
      const recommendedOptions = Array.isArray(response.recommendedList)
        ? normalizeProductCategoryOptions(
          response.recommendedList.map((item) => ({
            value: item.categoryId,
            label: item.label || item.fullLabel || item.categoryId,
            fullLabel: item.fullLabel || item.label || item.categoryId,
          }))
        )
        : [];

      if (requestVersion === shopCategoriesRequestVersion) {
        useAppStore.getState().setShopOperatingCategoriesOptions({
          shopId: requestedShopId,
          options: nextOptions,
          status: response.status,
          confirmed: response.confirmed,
          recommendedOptions,
          maxCount: response.maxCount,
        });
      }
      return nextOptions;
    })
    .catch((error: unknown) => {
      const nextMessage = error instanceof Error ? error.message : '加载经营类目失败';
      if (requestVersion === shopCategoriesRequestVersion) {
        useAppStore.getState().setShopOperatingCategoriesError(requestedShopId, nextMessage);
      }
      throw error;
    })
    .finally(() => {
      if (shopCategoriesRequest === nextRequest) {
        shopCategoriesRequest = null;
        shopCategoriesRequestKey = '';
      }
    });

  shopCategoriesRequest = nextRequest;
  return nextRequest;
}
